const skuDefiningProperties = ['productId', 'sizePrimary', 'sizeSecondary'];
const inventoryBackorderableStatus = 'Backorderable';
const urgencyThreshold = 5;
const inventoryAvailableStatus = 'Available';

// http://jira/browse/EPIC-9604 Jira to revisit this to adhere to "no-param-reassign" ESLint rule
function swap(paramArr, a, b) {
  const tmp = paramArr[a];
  // eslint-disable-next-line no-param-reassign
  paramArr[a] = paramArr[b];
  // eslint-disable-next-line no-param-reassign
  paramArr[b] = tmp;
}

// Takes an array and generates all permutations of the items in it
function permute(arr, len, cb) {
  if (len === 1) {
    cb(arr);
  } else {
    for (let i = 0; i < len; i += 1) {
      permute(arr, len - 1, cb);

      if (len % 2 === 0) {
        swap(arr, i, len - 1);
      } else {
        swap(arr, 0, len - 1);
      }
    }
  }
}

function permutationsForDefiningProperties(sku) {
  // Remove unused properties, usually secondary size
  const skuProperties = skuDefiningProperties
    .filter((property) => !!sku[property])
    .map((property) => sku[property]);

  const permuted = [];
  permute(skuProperties, skuProperties.length, (arr) => {
    permuted.push(arr.slice());
  });

  return permuted;
}

// Allows setting a value on an object with an array of property names
// Example:
// setNested(['a', 'b', 'c'], {}, true)
//  => { a: { b: { c: true } } }
function setNested(path, object, value) {
  const lastIndex = path.length - 1;
  let nested = object;

  for (let i = 0; i < path.length; i += 1) {
    const key = path[i];
    let newValue = value;
    const existing = nested[key];

    if (i !== lastIndex) {
      newValue = existing || {};
    }

    nested[key] = newValue;
    nested = nested[key];
  }

  return object;
}

function createSkuAvailabilityObject(acc, sku) {
  const permuted = permutationsForDefiningProperties(sku);

  permuted.forEach((permutation) => {
    setNested(permutation, acc, sku.inventory > 0);
  });

  return acc;
}

function createDefinedSkusObject(acc, sku) {
  const permuted = permutationsForDefiningProperties(sku);

  permuted.forEach((permutation) => {
    setNested(permutation, acc, sku.shortSku);
  });

  return acc;
}

// Gets a value out of an object with an array of property names
function getNested(path, object) {
  if (!object || path.length === 0) {
    return object;
  } if (path.length === 1) {
    return object[path[0]];
  }
  return getNested(path.slice(1), object[path[0]]);
}

// Searches recursively through an object tree to find if any key/value pair is true
// Returns as soon as it finds one.
function findNestedTrue(node) {
  let children;
  if (typeof node === 'boolean') {
    return node;
  }

  if (typeof node === 'object') {
    children = Object.keys(node);

    for (let i = 0; i < children.length; i += 1) {
      const foundTrue = findNestedTrue(node[children[i]]);
      if (foundTrue) {
        return true;
      }
    }
    return false;
  }
  return false;
}

function findIsSkuAvailable(skuAvailability, criteria, singleSearch) {
  const path = Object.keys(criteria).map((key) => criteria[key]);
  const state = getNested(path, skuAvailability);
  if (typeof state === 'boolean') {
    return state;
  } if (singleSearch || !state) {
    return false;
  }
  return findNestedTrue(state);
}

function hasQualifiedSku(skuAvailability, criteria) {
  let isSkuQualified = false;
  const path = Object.keys(criteria).map((key) => criteria[key]);
  const state = getNested(path, skuAvailability);
  if (typeof state === 'boolean') {
    isSkuQualified = state;
  }
  return isSkuQualified;
}

function buildCriteriaObject(selectedTiles, primarySizeArray, secondarySizeArray) {
  const criteriaObj = {
    productId: selectedTiles.productId,
  };

  const hasSinglePrimarySize = primarySizeArray && primarySizeArray.length === 1;
  const hasSingleSecondarySize = secondarySizeArray && secondarySizeArray.length === 1;

  if (selectedTiles.primarySize) {
    criteriaObj.primarySize = selectedTiles.primarySize;
  } else if (hasSinglePrimarySize) {
    criteriaObj.primarySize = primarySizeArray[0].value;
  }

  if (selectedTiles.secondarySize) {
    criteriaObj.secondarySize = selectedTiles.secondarySize;
  } else if (hasSingleSecondarySize) {
    criteriaObj.secondarySize = secondarySizeArray[0].value;
  }

  return criteriaObj;
}

function getVerifiedSelectionCriteria({
  skuAvailability, primarySizeArray, secondarySizeArray, hasSelectableSoldOutSizeTiles,
}, selectedTiles) {
  /* Use Case: user has size tile selected, then changes swatch, if that size tile isnt available
  for the new productId, we need to deselect now unavailable size tiles and treat the productId
  as the only thing selected */
  const criteriaObj = buildCriteriaObject(selectedTiles, primarySizeArray, secondarySizeArray);
  if (hasSelectableSoldOutSizeTiles) { return criteriaObj; }
  const verifiedCriteria = findIsSkuAvailable(skuAvailability, criteriaObj)
    ? criteriaObj
    : {
      productId: criteriaObj.productId,
    };

  return verifiedCriteria;
}

function getQualifiedSku({ definedSkus }, selectedTiles) {
  const arrayOfSelectedTiles = Object.keys(selectedTiles).map((key) => selectedTiles[key]);
  return getNested(arrayOfSelectedTiles, definedSkus);
}

function checkForSwatchUrgencyMessage(collection, productId) {
  const lowestSelected = collection.products?.filter((prod) => prod.productId === productId).map((prod) => prod.lowestInventoryCount);

  const lowestSelectedSwatchInventory = lowestSelected?.[0];
  const showUrgencyMessage = lowestSelectedSwatchInventory > 0
    && lowestSelectedSwatchInventory < urgencyThreshold;
  return showUrgencyMessage;
}

function buildSwatchAvailability({
  skuAvailability, verifiedCriteria, products,
}) {
  const newProducts = products.map((product) => ({
    ...product,
    tileOptions: { ...verifiedCriteria },
    selected: product.productId === verifiedCriteria.productId,
    productId: product.productId,
    available: findIsSkuAvailable(skuAvailability, { ...verifiedCriteria, productId: product.productId }),
  }));

  return newProducts;
}

function getSkuUrgency(collection) {
  let urgencyMessage = '';
  const filteredSku = collection.skus.filter((skuItem) => skuItem.shortSku === collection.qualifiedSku)[0];
  if (filteredSku) {
    if (filteredSku.inventoryStatus === inventoryBackorderableStatus) {
      urgencyMessage = 'backordered';
    } else if (filteredSku.inventory < urgencyThreshold && filteredSku.inventory > 0
      && filteredSku.inventoryStatus === inventoryAvailableStatus) {
      urgencyMessage = filteredSku.fullSizeLabel;
    }
  }

  return urgencyMessage;
}

function getFullSizeLabel(collection) {
  const hasSecondarySize = collection?.secondarySizeArray?.length > 0;
  const selectedPrimarySize = collection?.primarySizeArray?.find((item) => item.selected);
  const selectedSecondarySize = collection?.secondarySizeArray?.find((item) => item.selected);
  let fullSizeLabel = null;

  if (selectedPrimarySize && selectedSecondarySize) {
    fullSizeLabel = `${selectedPrimarySize.label} X ${selectedSecondarySize.label}`;
  } else if (selectedPrimarySize && !hasSecondarySize) {
    fullSizeLabel = selectedPrimarySize.label;
  }

  return fullSizeLabel;
}

function genderTMNT(gender, brand, tmntData) {
  let tmntName = 'howToMeasureMaleText';
  const femaleANF = gender === 'FEMALE' && brand === 'anf';
  const kidsFemaleANF = gender === 'KIDS_FEMALE' && brand === 'kids';
  const femaleHOL = gender === 'FEMALE' && brand === 'hol';
  const kidsMaleANF = gender === 'KIDS_MALE' && brand === 'kids';

  if (femaleANF || femaleHOL) tmntName = 'howToMeasureFemaleText';
  if (kidsFemaleANF) tmntName = 'howToMeasureKidsFemaleText';
  if (kidsMaleANF) tmntName = 'howToMeasureKidsMaleText';

  return tmntData[tmntName]?.value;
}

function updateCollectionData(collectionData, selectedTiles) {
  const collectionCopy = { ...collectionData };
  collectionCopy.verifiedCriteria = getVerifiedSelectionCriteria(collectionData, selectedTiles);
  collectionCopy.products = buildSwatchAvailability(collectionCopy);
  collectionCopy.hasQualifiedSku = hasQualifiedSku(
    collectionCopy.skuAvailability,
    collectionCopy.verifiedCriteria,
  );

  collectionCopy.qualifiedSku = null;
  collectionCopy.skuUrgency = null;

  if (collectionCopy.hasQualifiedSku) {
    collectionCopy.qualifiedSku = getQualifiedSku(collectionCopy, collectionCopy.verifiedCriteria);
    collectionCopy.skuUrgency = getSkuUrgency(collectionCopy);
  }
  collectionCopy.primarySizeArray = buildPrimarySizeAvailability(collectionCopy);
  collectionCopy.secondarySizeArray = buildSecondarySizeAvailability(collectionCopy);
  collectionCopy.showUrgencyMessage = checkForSwatchUrgencyMessage(
    collectionCopy,
    selectedTiles.productId,
  );
  collectionCopy.activeProduct = getActiveProduct(
    collectionCopy,
    selectedTiles.productId,
  );

  collectionCopy.fullSizeLabel = getFullSizeLabel(collectionCopy);
  collectionCopy.isSelectedSkuSoldOut = (collectionCopy.activeProduct.available === false && collectionCopy.hasSelectableSoldOutSizeTiles);

  return collectionCopy;
}

function getActiveProduct(collection, productId) {
  const selectedProduct = collection.products?.filter((prod) => prod.productId === productId);
  if (selectedProduct.length > 0) {
    return { ...selectedProduct[0] };
  }

  return collection.products[0];
}

function getSelectedTilesForSku(givenSku, skus) {
  const selectedTiles = {};
  const sku = skus?.find((skuItem) => skuItem.shortSku === givenSku);
  if (sku) {
    selectedTiles.productId = sku.productId;
    selectedTiles.primarySize = sku.sizePrimary;
    selectedTiles.secondarySize = sku.sizeSecondary;
  }

  return selectedTiles;
}

function buildPrimarySizeAvailability({
  skuAvailability, verifiedCriteria, primarySizeArray, hasSelectableSoldOutSizeTiles,
}) {
  const hasOneSize = primarySizeArray.length === 1;
  const newPrimarySizeArray = primarySizeArray.map((size) => {
    const thisSize = size;
    const newValues = {};
    const tileOptions = { ...verifiedCriteria };
    newValues.selected = hasOneSize || (thisSize.value === tileOptions.primarySize);
    tileOptions.primarySize = thisSize.value;
    const isAvailable = hasOneSize || findIsSkuAvailable(skuAvailability, tileOptions);
    newValues.available = isAvailable;
    newValues.enableSelectableSoldOutSizeTiles = hasSelectableSoldOutSizeTiles ? false : !isAvailable;
    return { ...thisSize, ...newValues };
  });
  return newPrimarySizeArray;
}

function buildSecondarySizeAvailability({
  skuAvailability, verifiedCriteria, secondarySizeArray, hasSelectableSoldOutSizeTiles,
}) {
  const newSecondarySizeArray = secondarySizeArray.map((size) => {
    const thisSize = size;
    const newValues = {};
    const tileOptions = { ...verifiedCriteria };
    newValues.selected = thisSize.value === tileOptions.secondarySize;
    tileOptions.secondarySize = thisSize.value;
    const isAvailable = findIsSkuAvailable(skuAvailability, tileOptions);
    newValues.available = isAvailable;
    newValues.enableSelectableSoldOutSizeTiles = hasSelectableSoldOutSizeTiles ? false : !isAvailable;
    return { ...thisSize, ...newValues };
  });

  return newSecondarySizeArray;
}

export {
  createSkuAvailabilityObject,
  createDefinedSkusObject,
  findIsSkuAvailable,
  getVerifiedSelectionCriteria,
  permute,
  setNested,
  getNested,
  findNestedTrue,
  updateCollectionData,
  getActiveProduct,
  getSelectedTilesForSku,
  buildPrimarySizeAvailability,
  buildSecondarySizeAvailability,
  buildSwatchAvailability,
  getSkuUrgency,
  getQualifiedSku,
  genderTMNT,
  getFullSizeLabel,
};
